import { useMemo, useState } from 'react';
import { AnimatePresence, motion } from 'framer-motion';
import {
  X,
  Brain,
  FolderKanban,
  Target,
  Shield,
  Users,
  TrendingUp,
  Calendar,
  CheckCircle2,
  AlertTriangle,
  BookOpen,
  Eye,
} from 'lucide-react';

interface GouvernanceGestionProjetsModalProps {
  isOpen: boolean;
  onClose: () => void;
}

type SectionId =
  | 'esprit'
  | 'cadre'
  | 'maturite'
  | 'roles'
  | 'raci'
  | 'roadmap'
  | 'rituels'
  | 'preuves';

const sections: { id: SectionId; label: string; icon: typeof Target }[] = [
  { id: 'esprit', label: 'Esprit', icon: Target },
  { id: 'cadre', label: 'Cadre', icon: Shield },
  { id: 'maturite', label: 'Maturité', icon: TrendingUp },
  { id: 'roles', label: 'Rôles', icon: Users },
  { id: 'raci', label: 'RACI', icon: Eye },
  { id: 'roadmap', label: 'Roadmap', icon: Calendar },
  { id: 'rituels', label: 'Rituels', icon: BookOpen },
  { id: 'preuves', label: 'Preuves visibles', icon: CheckCircle2 },
];

const maturityLevels = [
  {
    niveau: 'Niveau 1 — Réactif',
    description:
      'Les projets avancent au courage et à la bonne volonté. Les priorités changent souvent, les rôles sont implicites et la gouvernance n’arrive qu’en réaction aux problèmes.',
    signes: ['Peu de cadrage formel', 'Décisions prises dans l’urgence', 'Peu de visibilité sur les risques'],
  },
  {
    niveau: 'Niveau 2 — Structuré',
    description:
      'Les projets ont un cadre de base. On retrouve des responsables identifiés, des revues périodiques et un minimum de documentation, mais l’ensemble dépend encore beaucoup des individus.',
    signes: ['Mandats mieux définis', 'Suivi plus stable', 'Quelques rituels récurrents'],
  },
  {
    niveau: 'Niveau 3 — Gouverné',
    description:
      'La gestion de projets devient une discipline. Les rôles sont clairs, les arbitrages sont documentés, les risques sont suivis et les comités servent réellement à piloter.',
    signes: ['RACI compris', 'Décisions tracées', 'Escalade plus saine'],
  },
  {
    niveau: 'Niveau 4 — Maîtrisé',
    description:
      'La gouvernance permet d’anticiper plutôt que de subir. La roadmap est vivante, les dépendances sont visibles et les gestionnaires savent ajuster sans créer de chaos.',
    signes: ['Pilotage prévisible', 'Priorités cohérentes', 'Reddition simple et régulière'],
  },
  {
    niveau: 'Niveau 5 — Excellence',
    description:
      'La gouvernance projet devient un avantage stratégique. Les projets créent de la valeur mesurable, les rôles sont naturels, les rituels renforcent l’alignement et le cadre est vécu, pas seulement documenté.',
    signes: ['Valeur démontrée', 'Décisions solides', 'Confiance élevée entre leaders et équipe'],
  },
];

const roleCards = [
  {
    role: 'Dany Bourassa — A',
    color: 'from-violet-600 to-fuchsia-600',
    focus: 'Vision, cohérence et imputabilité finale',
    doit: [
      'Valider l’orientation globale du cadre de gestion de projets',
      'Assurer l’alignement avec les standards PRIsME et la vision BZ',
      'Arbitrer les décisions majeures quand le cadre doit être clarifié',
    ],
    eviter: [
      'Descendre dans la microgestion quotidienne',
      'Laisser vivre un cadre contradictoire ou flou'],
  },
  {
    role: 'Éric Hains — R',
    color: 'from-blue-600 to-cyan-600',
    focus: 'Exécution du cadre et orchestration opérationnelle de la gouvernance',
    doit: [
      'Faire vivre la gouvernance de gestion de projets au quotidien',
      'Animer les suivis, porter les arbitrages et maintenir la clarté',
      'S’assurer que les projets sont cadrés, suivis et documentés',
    ],
    eviter: [
      'Laisser des zones grises sur les responsabilités',
      'Piloter sans trace ni priorisation'],
  },
  {
    role: 'Francis Marquis — C',
    color: 'from-amber-500 to-orange-600',
    focus: 'Regard critique, qualité, cohérence et fluidité comité / livraison',
    doit: [
      'Questionner la robustesse du cadre',
      'Contribuer à la qualité des mécanismes de gouvernance',
      'Apporter un angle de structuration, de clarté et d’adoption',
    ],
    eviter: [
      'Rester seulement en réaction',
      'Laisser des ambiguïtés sur les attentes de comité'],
  },
  {
    role: 'Jean-Michel Caouette — C',
    color: 'from-emerald-500 to-green-600',
    focus: 'Lecture terrain, adoption, pragmatisme et valeur livrée',
    doit: [
      'Ramener le cadre vers le concret et l’utilisable',
      'Aider à garder une gouvernance utile pour l’équipe et le client',
      'Mettre en lumière les ajustements nécessaires pour mieux livrer',
    ],
    eviter: [
      'Tolérer une gouvernance déconnectée du réel',
      'Accepter une lourdeur qui nuit à l’élan'],
  },
];

const raciRows = [
  ['Définir le cadre de gestion de projets', 'Éric', 'Dany', 'Francis, Jean-Michel', 'Comité PRIsME'],
  ['Prioriser les projets et arbitrer le portefeuille', 'Éric', 'Dany', 'Francis, Jean-Michel', 'Gestionnaires concernés'],
  ['Valider les rôles et responsabilités projet', 'Éric', 'Dany', 'Francis, Jean-Michel', 'Équipe'],
  ['Animer les rituels de gouvernance projet', 'Éric', 'Éric', 'Francis, Jean-Michel', 'Comité PRIsME'],
  ['Suivre les risques, blocages et dépendances', 'Éric', 'Éric', 'Francis, Jean-Michel', 'Leaders touchés'],
  ['Faire évoluer le cadre au fil de la maturité', 'Éric', 'Dany', 'Francis, Jean-Michel', 'Comité PRIsME'],
];

const roadmap = [
  {
    phase: 'Phase 1 — Clarifier',
    items: [
      'Formuler en une page ce que BZ entend par gouvernance de gestion de projets',
      'Nommer les rôles et la logique d’escalade',
      'Uniformiser le vocabulaire projet, risque, décision, priorité',
    ],
  },
  {
    phase: 'Phase 2 — Standardiser',
    items: [
      'Mettre en place un cadre simple de cadrage et de suivi',
      'Établir des attentes minimales par projet',
      'Créer une discipline de documentation légère mais obligatoire',
    ],
  },
  {
    phase: 'Phase 3 — Piloter',
    items: [
      'Introduire des revues de gouvernance régulières',
      'Rendre visibles les risques, dépendances et arbitrages',
      'Relier les projets à la valeur attendue et aux priorités BZ',
    ],
  },
  {
    phase: 'Phase 4 — Élever',
    items: [
      'Mesurer la maturité projet par équipe',
      'Ajuster le cadre selon les apprentissages',
      'Faire de la gestion de projets un réflexe de leadership, pas un exercice administratif',
    ],
  },
];

const rituals = [
  {
    nom: 'Comité de gouvernance de projets',
    cadence: 'Mensuel',
    objectif: 'Arbitrer les priorités, les risques et les décisions majeures.',
  },
  {
    nom: 'Revue d’avancement',
    cadence: 'Hebdo ou bihebdo',
    objectif: 'Suivre l’état réel des projets, les blocages et les prochaines décisions.',
  },
  {
    nom: 'Revue des risques et dépendances',
    cadence: 'Mensuel',
    objectif: 'Éviter les angles morts et forcer les escalades au bon moment.',
  },
  {
    nom: 'Revue de cadrage',
    cadence: 'Au démarrage',
    objectif: 'Valider le pourquoi, le scope, les rôles, les attentes et les critères de succès.',
  },
  {
    nom: 'Leçons apprises / rétro de gouvernance',
    cadence: 'Fin de projet ou trimestriel',
    objectif: 'Capitaliser, corriger le cadre et éviter de répéter les mêmes erreurs.',
  },
];

const visibleProofs = [
  'Chaque projet a un owner clair et un cadre minimal explicite.',
  'Les décisions importantes sont tracées et compréhensibles.',
  'Les risques majeurs sont visibles avant qu’ils deviennent des crises.',
  'Les comités servent à décider, pas seulement à informer.',
  'La roadmap projet reflète les vraies priorités BZ.',
  'Les rôles sont compris par l’équipe et tenus dans la durée.',
  'La gouvernance aide à livrer mieux au lieu d’alourdir le terrain.',
];

const guardrails = [
  'La gouvernance ne doit jamais devenir une bureaucratie décorative.',
  'Un projet sans rôle clair, sans arbitrage et sans visibilité n’est pas gouverné.',
  'Un comité qui ne tranche pas devient une salle d’attente coûteuse.',
  'La documentation doit soutenir l’action, pas remplacer le jugement.',
];

export default function GouvernanceGestionProjetsModal({
  isOpen,
  onClose,
}: GouvernanceGestionProjetsModalProps) {
  const [activeSection, setActiveSection] = useState<SectionId>('esprit');

  const sectionContent = useMemo(() => {
    switch (activeSection) {
      case 'esprit':
        return (
          <div className="space-y-6">
            <section className="rounded-3xl border border-violet-100 bg-violet-50/70 p-6">
              <h3 className="text-2xl font-black text-slate-900 mb-3">Pourquoi ce pilier existe</h3>
              <p className="text-slate-700 leading-7">
                La gestion de projets, dans une logique PRIsME, n’est pas un simple mécanisme de suivi. C’est une
                discipline de gouvernance qui sert à donner de la clarté, de la cohérence et de la valeur aux projets.
                Elle existe pour éviter les projets flous, les priorités contradictoires, les responsabilités implicites
                et les décisions prises trop tard.
              </p>
            </section>

            <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
              <section className="rounded-3xl border border-white/70 bg-white p-6 shadow-sm">
                <h4 className="text-lg font-bold text-slate-900 mb-3">Ce que la gouvernance doit créer</h4>
                <ul className="space-y-2 text-slate-700">
                  {[
                    'Un langage commun pour cadrer les projets',
                    'Des décisions plus rapides et plus défendables',
                    'Des rôles clairs, assumés et visibles',
                    'Un lien plus direct entre projet, valeur et priorités BZ',
                  ].map((item) => (
                    <li key={item} className="flex gap-2"><CheckCircle2 className="w-4 h-4 text-emerald-500 mt-1 shrink-0" />{item}</li>
                  ))}
                </ul>
              </section>

              <section className="rounded-3xl border border-rose-100 bg-rose-50/70 p-6 shadow-sm">
                <h4 className="text-lg font-bold text-slate-900 mb-3">Ce que ça ne doit jamais devenir</h4>
                <ul className="space-y-2 text-slate-700">
                  {guardrails.map((item) => (
                    <li key={item} className="flex gap-2"><AlertTriangle className="w-4 h-4 text-rose-500 mt-1 shrink-0" />{item}</li>
                  ))}
                </ul>
              </section>
            </div>
          </div>
        );
      case 'cadre':
        return (
          <div className="space-y-6">
            <section className="rounded-3xl border border-white/70 bg-white p-6 shadow-sm">
              <h3 className="text-2xl font-black text-slate-900 mb-3">Cadre de gouvernance — Gestion de projets</h3>
              <p className="text-slate-700 leading-7 mb-5">
                Ce pilier mélange trois angles : la gouvernance des projets chez BZ, le pilier Gestion de projets du Cadre
                référentiel BZ, et l’usage PRIsME comme outil de comité, de coaching et de structuration des rôles.
              </p>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4 text-sm text-slate-700">
                {[
                  'Cadrage : pourquoi le projet existe, ce qu’il doit changer et comment on saura qu’il crée de la valeur.',
                  'Priorisation : quels projets méritent l’attention, quand, et selon quels critères d’arbitrage.',
                  'Rôles : qui décide, qui orchestre, qui contribue et qui doit simplement être informé.',
                  'Suivi : où on en est réellement, ce qui bloque et ce qui doit être escaladé.',
                  'Reddition : comment on documente les décisions, les risques et les prochaines actions.',
                  'Amélioration : comment on fait évoluer le cadre sans le rendre lourd.',
                ].map((item) => (
                  <div key={item} className="rounded-2xl border border-slate-200 bg-slate-50 p-4">{item}</div>
                ))}
              </div>
            </section>
          </div>
        );
      case 'maturite':
        return (
          <div className="space-y-4">
            {maturityLevels.map((level) => (
              <section key={level.niveau} className="rounded-3xl border border-white/70 bg-white p-6 shadow-sm">
                <h3 className="text-xl font-black text-slate-900 mb-2">{level.niveau}</h3>
                <p className="text-slate-700 leading-7 mb-4">{level.description}</p>
                <div className="grid grid-cols-1 md:grid-cols-3 gap-3">
                  {level.signes.map((item) => (
                    <div key={item} className="rounded-2xl bg-slate-50 border border-slate-200 p-3 text-sm text-slate-700">
                      {item}
                    </div>
                  ))}
                </div>
              </section>
            ))}
          </div>
        );
      case 'roles':
        return (
          <div className="grid grid-cols-1 xl:grid-cols-2 gap-6">
            {roleCards.map((card) => (
              <section key={card.role} className="overflow-hidden rounded-3xl border border-white/70 bg-white shadow-sm">
                <div className={`bg-gradient-to-r ${card.color} px-6 py-5 text-white`}>
                  <h3 className="text-xl font-black">{card.role}</h3>
                  <p className="text-white/85 mt-1">{card.focus}</p>
                </div>
                <div className="p-6 grid grid-cols-1 md:grid-cols-2 gap-5">
                  <div>
                    <p className="text-sm font-bold text-slate-900 mb-3">Doit porter</p>
                    <ul className="space-y-2 text-sm text-slate-700">
                      {card.doit.map((item) => (
                        <li key={item} className="flex gap-2"><CheckCircle2 className="w-4 h-4 text-emerald-500 mt-0.5 shrink-0" />{item}</li>
                      ))}
                    </ul>
                  </div>
                  <div>
                    <p className="text-sm font-bold text-slate-900 mb-3">Doit éviter</p>
                    <ul className="space-y-2 text-sm text-slate-700">
                      {card.eviter.map((item) => (
                        <li key={item} className="flex gap-2"><AlertTriangle className="w-4 h-4 text-amber-500 mt-0.5 shrink-0" />{item}</li>
                      ))}
                    </ul>
                  </div>
                </div>
              </section>
            ))}
          </div>
        );
      case 'raci':
        return (
          <div className="overflow-hidden rounded-3xl border border-white/70 bg-white shadow-sm">
            <div className="overflow-x-auto">
              <table className="min-w-full text-sm">
                <thead className="bg-slate-900 text-white">
                  <tr>
                    {['Activité de gouvernance', 'R', 'A', 'C', 'I'].map((h) => (
                      <th key={h} className="px-4 py-4 text-left font-bold">{h}</th>
                    ))}
                  </tr>
                </thead>
                <tbody>
                  {raciRows.map((row, index) => (
                    <tr key={row[0]} className={index % 2 === 0 ? 'bg-white' : 'bg-slate-50'}>
                      {row.map((cell, cellIndex) => (
                        <td key={`${row[0]}-${cellIndex}`} className="px-4 py-4 align-top text-slate-700">
                          {cell}
                        </td>
                      ))}
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </div>
        );
      case 'roadmap':
        return (
          <div className="space-y-4">
            {roadmap.map((phase, index) => (
              <section key={phase.phase} className="rounded-3xl border border-white/70 bg-white p-6 shadow-sm">
                <div className="flex items-center gap-3 mb-4">
                  <div className="w-9 h-9 rounded-full bg-violet-100 text-violet-700 flex items-center justify-center font-black">
                    {index + 1}
                  </div>
                  <h3 className="text-xl font-black text-slate-900">{phase.phase}</h3>
                </div>
                <ul className="space-y-2 text-slate-700">
                  {phase.items.map((item) => (
                    <li key={item} className="flex gap-2"><CheckCircle2 className="w-4 h-4 text-violet-500 mt-1 shrink-0" />{item}</li>
                  ))}
                </ul>
              </section>
            ))}
          </div>
        );
      case 'rituels':
        return (
          <div className="grid grid-cols-1 md:grid-cols-2 gap-5">
            {rituals.map((ritual) => (
              <section key={ritual.nom} className="rounded-3xl border border-white/70 bg-white p-6 shadow-sm">
                <div className="flex items-center justify-between gap-3 mb-3">
                  <h3 className="text-lg font-black text-slate-900">{ritual.nom}</h3>
                  <span className="rounded-full border border-blue-200 bg-blue-50 px-3 py-1 text-xs font-bold text-blue-700">{ritual.cadence}</span>
                </div>
                <p className="text-slate-700 leading-7">{ritual.objectif}</p>
              </section>
            ))}
          </div>
        );
      case 'preuves':
        return (
          <div className="space-y-6">
            <section className="rounded-3xl border border-emerald-100 bg-emerald-50/70 p-6">
              <h3 className="text-2xl font-black text-slate-900 mb-4">Preuves visibles d’une gouvernance saine</h3>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
                {visibleProofs.map((item) => (
                  <div key={item} className="rounded-2xl border border-emerald-200 bg-white px-4 py-3 text-sm text-slate-700">
                    {item}
                  </div>
                ))}
              </div>
            </section>
          </div>
        );
      default:
        return null;
    }
  }, [activeSection]);

  if (!isOpen) return null;

  return (
    <AnimatePresence>
      <div className="fixed inset-0 z-50 flex items-center justify-center">
        <motion.div
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          exit={{ opacity: 0 }}
          className="absolute inset-0 bg-black/70 backdrop-blur-sm"
          onClick={onClose}
        />

        <motion.div
          initial={{ opacity: 0, y: 20, scale: 0.98 }}
          animate={{ opacity: 1, y: 0, scale: 1 }}
          exit={{ opacity: 0, y: 20, scale: 0.98 }}
          transition={{ duration: 0.25 }}
          className="relative z-10 w-[95vw] h-[92vh] overflow-hidden rounded-[2rem] bg-slate-100 shadow-2xl border border-white/20 flex flex-col"
        >
          <div className="relative overflow-hidden bg-gradient-to-r from-slate-900 via-violet-900 to-indigo-900 px-8 py-7 text-white">
            <div className="absolute inset-0 opacity-20 bg-[radial-gradient(circle_at_top_right,_rgba(255,255,255,0.25),_transparent_40%)]" />
            <div className="absolute right-20 top-5 flex items-center gap-2">
              <img
                src="/DB.png"
                alt="Dany Bourassa"
                className="h-11 w-11 rounded-full border-2 border-white/70 object-cover shadow-lg"
              />
              <img
                src="/EH.png"
                alt="Éric Houde"
                className="h-11 w-11 rounded-full border-2 border-white/70 object-cover shadow-lg"
              />
            </div>

            <button
              onClick={onClose}
              className="absolute right-6 top-6 rounded-xl bg-white/10 p-2 text-white hover:bg-white/20 transition-colors"
            >
              <X className="w-5 h-5" />
            </button>

            <div className="relative flex items-start gap-4 pr-12">
              <div className="rounded-2xl bg-white/10 p-4 backdrop-blur-sm border border-white/10">
                <FolderKanban className="w-10 h-10 text-white" />
              </div>
              <div className="flex-1">
                <div className="flex items-center gap-3 flex-wrap mb-2">
                  <h1 className="text-3xl md:text-4xl font-black">Gouvernance – Gestion de projets</h1>
                  <div className="inline-flex items-center gap-1.5 rounded-full border border-violet-300/40 bg-violet-500/20 px-3 py-1 text-xs font-semibold text-violet-100">
                    <Brain className="w-4 h-4" />
                    AI
                  </div>
                </div>
                <p className="max-w-4xl text-sm md:text-base text-white/85 leading-7">
                  Un cadre PRIsME riche pour coacher les gestionnaires, outiller les comités et structurer les rôles autour de la gestion de projets chez BZ.
                </p>
              </div>
            </div>
          </div>

          <div className="flex-1 min-h-0 grid grid-cols-1 xl:grid-cols-[300px_minmax(0,1fr)] gap-0">
            <aside className="border-r border-slate-200 bg-white/80 backdrop-blur-sm p-4 overflow-y-auto">
              <div className="space-y-2">
                {sections.map((section) => {
                  const Icon = section.icon;
                  const active = activeSection === section.id;
                  return (
                    <button
                      key={section.id}
                      onClick={() => setActiveSection(section.id)}
                      className={`w-full rounded-2xl px-4 py-3 text-left transition-all border ${
                        active
                          ? 'bg-violet-600 text-white border-violet-600 shadow-lg'
                          : 'bg-white text-slate-700 border-slate-200 hover:border-violet-200 hover:bg-violet-50'
                      }`}
                    >
                      <div className="flex items-center gap-3">
                        <Icon className="w-4 h-4" />
                        <span className="font-semibold text-sm">{section.label}</span>
                      </div>
                    </button>
                  );
                })}
              </div>
            </aside>

            <main className="min-h-0 overflow-y-auto p-6 md:p-8">
              <div className="mb-6 grid grid-cols-1 lg:grid-cols-3 gap-4">
                <div className="rounded-3xl border border-white/70 bg-white p-5 shadow-sm">
                  <p className="text-xs uppercase tracking-wide text-slate-500 mb-1">Objectif</p>
                  <p className="text-sm text-slate-700">Coacher les gestionnaires et structurer la prise de décision projet.</p>
                </div>
                <div className="rounded-3xl border border-white/70 bg-white p-5 shadow-sm">
                  <p className="text-xs uppercase tracking-wide text-slate-500 mb-1">Posture</p>
                  <p className="text-sm text-slate-700">Une gouvernance utile, vivante et orientée valeur — jamais décorative.</p>
                </div>
                <div className="rounded-3xl border border-white/70 bg-white p-5 shadow-sm">
                  <p className="text-xs uppercase tracking-wide text-slate-500 mb-1">Responsables</p>
                  <p className="text-sm text-slate-700">Dany (A) · Éric (R) · Francis (C) · Jean-Michel (C)</p>
                </div>
              </div>

              {sectionContent}
            </main>
          </div>
        </motion.div>
      </div>
    </AnimatePresence>
  );
}
